import React, { useEffect, useState, useCallback } from "react";
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from "component/Modal";
import DateIntervalInput from 'component/DateIntervalInput/NewDateIntevalInput'
import TextField from "component/TextField";
import RegionBranchMultipleSelect from 'component/branch/RegionBranchMultipleSelectNew'
import ThemeSelectField from 'component/priceBanner/selects/ThemeSelectField';
import 'container/priceBanner/styles.css';
import moment from "moment";

const calculateStatus = (startDate, endDate) => {
  const today = moment().startOf('day');
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  if (end.isBefore(today)) {
    return 2;
  }
  if (start.isAfter(today)) {
    return 1;
  }
  return 0;
};

export function EditListModal({
  open,
  onOpenChange,
  onSave,
  user,
  initialData,
  translate
}) {
  const [idBranches, setIdBranches] = useState([]);
  const [themeCode, setThemeCode] = useState(null);
  const [dateList, setDateList] = useState([]);
  const [listName, setListName] = useState('');
  const [errors, setErrors] = useState({
    branches: false,
    theme: false,
    listName: false,
    date: false
  });
  const handleBranchesChange = useCallback((e) => {
    setIdBranches(e.target.value);
    setErrors(prev => ({ ...prev, branches: false }));
  }, []);
  const handleThemeChange = useCallback((v) => {
    setThemeCode(v);
    setErrors(e => ({ ...e, theme: false }));
  }, []);
  const handleListNameChange = useCallback((e) => {
    setListName(e.target.value);
    setErrors(e => ({ ...e, listName: false }));
  }, []);
  const handleDateChange = useCallback((v) => {
    setDateList(v);
    setErrors(e => ({ ...e, date: false }));
  }, []);

  if (!open) return null;

  useEffect(() => {
    if (!initialData) {
      setIdBranches([]);
      setThemeCode(null);
      setDateList([]);
      setListName('');
      return;
    }
    if (initialData.branches) {
      const branches = initialData.branches.map(b => ({
        value: b.idBranch,
        label: b.dsBranch
      }));
      
      const regionMapping = {
        "Regional Interior SP": 7,
        "Regional Centro Nordeste": 4,
        "Regional Sudeste": 1,
        "Região SP": 3,
        "Região Sul": 2
      };

      const regions = initialData.branchNamesGrouped
        ? initialData.branchNamesGrouped.map(regionName => ({
          value: regionMapping[regionName] || regionName,
          label: regionName
        }))
        : [];

      setIdBranches([...branches, ...regions]);
    } else if (initialData.idBranches) {
      setIdBranches(initialData.idBranches.map(id => ({
        value: id,
      })));
    }

    if (initialData.themeCode) {
      const theme = {
        value: initialData.themeCode,
        label: initialData.themeName || initialData.themeCode
      };
      setThemeCode(theme);
    } else if (initialData.themeName) {
      const theme = {
        value: initialData.themeName,
        label: initialData.themeName
      };
      setThemeCode(theme);
    }
    if (initialData.nameList) {
      setListName(initialData.nameList);
    } else if (initialData.name) {
      setListName(initialData.name);
    }
    const startRaw = initialData.dtStart;
    const endRaw = initialData.dtEnd;
    const start = moment(startRaw, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
    const end = moment(endRaw, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
    setDateList([start, end]);
  }, [initialData]);

  useEffect(() => {
    if (open) {
      setErrors({
        branches: false,
        theme: false,
        date: false,
        listName: false
      });
    }
  }, [open]);

  const handleConfirm = async () => {
    const newErr = {
      branches: idBranches.length === 0,
      theme: !themeCode || !themeCode.value,
      date: !dateList || dateList.length !== 2,
      listName: !listName || listName.trim() === ''
    };
    const hasError = newErr.branches || newErr.theme || newErr.date || newErr.listName;
    if (!user?.username || !user?.fullName) {
      return;
    }
    if (hasError) {
      setErrors(newErr);
      return;
    }

    const idToUse = initialData?.cdPricebannerThemesList || initialData?.idPriceBannerList;
    const branchesArray = idBranches.flatMap(b => b.idBranches ?? [b.value]).filter(id => typeof id === 'string');
    const status = calculateStatus(dateList[0], dateList[1]);
    const processedData = {
      idPriceBannerList: idToUse,
      name: listName,
      themeCode: themeCode?.value || null,
      themeName: themeCode?.label || '',
      dtStart: dateList[0]?.format('YYYY-MM-DD'),
      dtEnd: dateList[1]?.format('YYYY-MM-DD'),
      cdLdap: user?.username || '',
      dsLdap: user?.fullName || '',
      idBranches: branchesArray,
      status
    };
    onSave && onSave(processedData);
  };

  return (
    <Modal
      title={translate('price.banner.themes.list.edit')}
      size="md"
      isOpen={open}
      onCloseModal={() => onOpenChange(false)}
      useControlledState
      className="pricebanner-modal"
      confirmButtonText={translate('save_button_label')}
      onConfirm={handleConfirm}
      isConfirmButtonDisabled={Object.values(errors).some(error => error)}
      noCloseOnConfirm
    >
      <div className="w-100">
        <div className="w-100">
          <RegionBranchMultipleSelect
            required
            name="idBranches"
            label={translate('price.banner.stores')}
            placeholder={translate('field.selectMultiStores')}
            value={idBranches}
            onChange={handleBranchesChange}
            validateValue={value => !!value && value.length > 0}
            errorText={errors.branches ? translate('mandatory') : undefined}
            showHelperArea={false}
            selectAll
            isMulti
            user={user}
            translate={translate}
            groupOptions
            excludeInactive={true}
            attrs={{
              fill: true
            }}
          />
        </div>
        <div className="w-100">
          <ThemeSelectField
            label={translate('price.banner.theme')}
            placeholder={translate('price.banner.select.theme')}
            value={themeCode}
            onChange={handleThemeChange}
            required
            errorText={errors.theme ? translate('mandatory') : undefined}
            showHelperArea={false}
            validateValue={(value) => !!value?.value}
            user={user}
            fill
          />
        </div>
        <div className="w-100 custom-height-input">
          <TextField
            required
            readOnly={false}
            disabled={false}
            label={translate('price.banner.listname')}
            placeholder={translate('irc.choose-name')}
            name="listName"
            fill
            onChange={handleListNameChange}
            translate={translate}
            value={listName}
            validateValue={(v) => Boolean(v)}
            errorText={errors.listName ? translate('mandatory') : undefined}
          />
        </div>
        <div className="w-100 custom-height-input">
          <DateIntervalInput
            label={translate('price.banner.period')}
            initialValue={dateList}
            onChange={handleDateChange}
            required
            validateValue={value => Array.isArray(value) && value.length === 2 && !!value[0] && !!value[1]}
            error={errors.date ? 'error' : undefined}
            errorMsg={errors.date ? translate('mandatory') : undefined}
            selectAll
            isMulti
            noSelect
            minDate={new Date()}
            useMinDate
            disableAutoClose
            user={user}
          />
        </div>

      </div>
    </Modal>
  );
}

EditListModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  user: PropTypes.object,
  initialData: PropTypes.object,
  dataValidation: PropTypes.object,
  errorValidation: PropTypes.object,
  translate: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  dataValidation: state.pricebannerList?.dataValidation,
  errorValidation: state.pricebannerList?.errorValidation
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(translate(null, { wait: true, translateFuncName: 'translate' })(EditListModal));
